import { Drawer } from 'restaurantAdmin/components/drawer/Drawer';
import { useGuestTags } from 'restaurantAdmin/hooks/useGuestTags';
import { useVipTiers } from 'restaurantAdmin/hooks/useVipTiers';
import type { GuestTag } from 'restaurantAdmin/restaurants/apiHelpers';
import type { VipTier } from 'restaurantAdmin/settings/general/vipProgramSection/apiHelpers';
import { Dialog } from '@base-ui/react/dialog';
import { Button, ButtonVariants } from '@components/button/Button';
import { Combobox } from '@components/combobox/Combobox';
import { ControlledFormCheckbox } from '@components/formInputs/ControlledFormCheckbox';
import { ControlledFormDateRange } from '@components/formInputs/ControlledFormDateRange';
import { LayoutVariant } from '@components/formInputs/sharedTypes';
import { Icon } from '@components/icon/Icon';
import cx from 'classnames';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import typography from '~styles/typography.scss';
import { getGuestExportRows } from './apiHelpers';
import styles from './GuestBookFilters.scss';
import { useGuestBookFilterParams } from './hooks';
import { downloadGuestExportCsv } from './guestExportHelper';
import { errorToast } from '@components/toasts/Toasts';

interface GuestBookFiltersProps {
  search?: string;
}

export const GuestBookFilters = ({ search = '' }: GuestBookFiltersProps) => {
  const { id: restaurantId } = useRestaurant();
  const { data: guestTags } = useGuestTags();
  const { data: vipTiers = [] } = useVipTiers();
  const [isExporting, setIsExporting] = useState(false);
  const {
    hasActiveFilters,
    filterSearchParams,
    updateMarketingOptInSearchParam,
    updateVisitDateSearchParams,
    updateTags,
    updateVipTiers,
  } = useGuestBookFilterParams();

  const tagSearchParams = filterSearchParams.getAll('tag');
  const selectedGuestTagsFromParams = tagSearchParams.flatMap(
    (tagSearchParam) =>
      guestTags.find((guestTag) => tagSearchParam === guestTag.title) || [],
  );

  const vipTierIdSearchParams = filterSearchParams.getAll('vipTierId');
  const selectedVipTiersFromParams = vipTierIdSearchParams.flatMap(
    (id) => vipTiers.find((tier) => tier.id === id) || [],
  );

  const startDateSearchParam = filterSearchParams.get('startDate');
  const endDateSearchParam = filterSearchParams.get('endDate');
  const marketingOptedInOnlySearchParam = filterSearchParams.get(
    'marketingOptedInOnly',
  );

  const { control, watch } = useForm({
    defaultValues: {
      startDate: startDateSearchParam || '',
      endDate: endDateSearchParam || '',
      marketingOptedInOnly: marketingOptedInOnlySearchParam === 'true',
    },
  });

  const {
    startDate: formStartDate,
    endDate: formEndDate,
    marketingOptedInOnly,
  } = watch();

  // biome-ignore lint/correctness/useExhaustiveDependencies: This is fine 🔥
  useEffect(() => {
    if (
      formStartDate === (startDateSearchParam ?? '') &&
      formEndDate === (endDateSearchParam ?? '')
    ) {
      return;
    }
    updateVisitDateSearchParams({
      startDate: formStartDate,
      endDate: formEndDate,
    });
  }, [formStartDate, formEndDate]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: This is fine 🔥
  useEffect(() => {
    if (marketingOptedInOnly === (marketingOptedInOnlySearchParam === 'true')) {
      return;
    }
    updateMarketingOptInSearchParam(marketingOptedInOnly === true);
  }, [marketingOptedInOnly]);

  const handleExportGuests = async () => {
    setIsExporting(true);

    try {
      const exportRows = await getGuestExportRows({
        restaurantId,
        search,
        filters: filterSearchParams,
      });

      downloadGuestExportCsv(exportRows);
    } catch (error) {
      errorToast({ message: 'Failed to export guests' });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Drawer
      modal={false}
      side="right"
      trigger={
        <button
          aria-label="Open filters"
          className={styles.filterButton}
          type="button"
        >
          {hasActiveFilters && (
            <span
              aria-label="Filters applied"
              className={styles.filterStatusIndicator}
              role="status"
            />
          )}
          <Icon name="filter" />
        </button>
      }
    >
      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.title}>
            <Icon name="filter" />
            <h2 className={typography.h4}>Filter Guest Book</h2>
          </div>
          <div className={styles.headerActions}>
            <Button
              isLoading={isExporting}
              label="Export CSV"
              onClick={handleExportGuests}
              variant={ButtonVariants.Tertiary}
            />
            <Dialog.Close
              render={({ onClick }) => (
                <Button
                  onClick={
                    onClick as React.MouseEventHandler<HTMLButtonElement>
                  }
                  label="Close"
                  variant={ButtonVariants.Tertiary}
                />
              )}
            />
          </div>
        </header>
        <form className={styles.filtersForm}>
          <div className={styles.formSection}>
            <label
              className={cx(typography.c2_20, styles.label)}
              htmlFor="guest-tags"
            >
              Guest Tags
            </label>
            <Combobox
              allowOptionCreate={false}
              aria-labelledby="guest-tags-label"
              disableCloseOnSelect={false}
              getOptionLabel={(tag) => tag.title}
              name="guest-tags"
              onChange={(value) => updateTags(value as GuestTag[])}
              options={guestTags}
              placeholder="Select Guest Tags"
              value={selectedGuestTagsFromParams}
            />
          </div>
          <div className={styles.formSection}>
            <ControlledFormDateRange
              control={control}
              fromProps={{
                label: 'Start Date',
                name: 'startDate',
                placeholder: 'Start Date',
              }}
              toProps={{
                label: 'End Date',
                name: 'endDate',
                placeholder: 'End Date',
              }}
              groupLabel="Visit Date"
              variant={LayoutVariant.Vertical}
            />
          </div>
          <ControlledFormCheckbox
            control={control}
            label="Only show guests opted into marketing"
            name="marketingOptedInOnly"
          />
          {vipTiers.length > 0 && (
            <div className={styles.formSection}>
              <label
                className={cx(typography.c2_20, styles.label)}
                htmlFor="vip-tiers"
              >
                VIP Tiers
              </label>
              <Combobox
                allowOptionCreate={false}
                aria-labelledby="vip-tiers-label"
                disableCloseOnSelect={false}
                getOptionLabel={(tier) => tier.name}
                name="vip-tiers"
                onChange={(value) => updateVipTiers(value as VipTier[])}
                options={vipTiers}
                placeholder="Select VIP Tiers"
                value={selectedVipTiersFromParams}
              />
            </div>
          )}
        </form>
      </div>
    </Drawer>
  );
};
